'use client';

import {
  createColumnHelper,
  getCoreRowModel,
  PaginationState,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import clsx from 'clsx';
import _ from 'lodash';
import { highlight, languages } from 'prismjs';
import React, { ReactElement } from 'react';
import { FiLoader } from 'react-icons/fi';
import { PiListMagnifyingGlass } from 'react-icons/pi';
import Editor from 'react-simple-code-editor';
import { toast } from 'react-toastify';
import { z } from 'zod';
import 'prismjs/components/prism-json';
import 'prismjs/themes/prism.css';
import 'react-toastify/dist/ReactToastify.css';

import APIGateway from '@/api';
import ModalSourceShow from '@/app/evaluations/_modalSourceShow/modalSourceShow';
import { AuthContext } from '@/components/auth/authWrapper';
import { Button } from '@/components/button/button';
import { Icon } from '@/components/icon/icon';
import { MultiStepModal, MultiStepModalProps, MultiStepModalState } from '@/components/multiStepModal/multiStepModal';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { buildColumnClassString, ColumnModEnums } from '@/components/table/table';
import { Heading } from '@/components/typography/heading';
import {
  RunCreateReq, Source, SourceGetRes
} from '@/types/evals';
import { getResourceIcon, getResourceName } from '@/utils/sourceUtils';

import styles from './modalRunCreate.module.scss';

const columnHelper = createColumnHelper<Source>();

interface DataSourcesTableProps {
  sources: Source[];
  selectedSourceId?: string;
  reloadedSourceIndex?: number;
  rowCount: number;
  onFetchPage: ( pageIndex: number, pageSize: number ) => void;
  onDataSourceSelect: ( sourceId: string ) => void;
  onChangeDisplayedSourceVersion: ( sourceIndex: number, version: number ) => void;
  onPeekSource: ( index: number ) => void;
}

function DataSourcesTable( {
  sources,
  selectedSourceId,
  reloadedSourceIndex,
  rowCount,
  onFetchPage,
  onDataSourceSelect,
  onChangeDisplayedSourceVersion,
  onPeekSource
}: DataSourcesTableProps ) {
  const [ pagination, setPagination ] = React.useState<PaginationState>( { pageIndex: 0, pageSize: 20 } );

  const handlePaginationChange = ( updater: React.SetStateAction<PaginationState> ) => {
    const newPagination = typeof updater === 'function' ? updater( pagination ) : updater;
    setPagination( newPagination );
    onFetchPage( newPagination.pageIndex, newPagination.pageSize );
  };

  const columns = React.useMemo( () => [
    columnHelper.display( {
      id: 'select',
      header: '',
      size: 50,
      cell: info => {
        const source = info.row.original;
        return (
          <input
            type='radio'
            name='data-source-selection'
            checked={ source.id === selectedSourceId }
            onChange={ () => onDataSourceSelect( source.id ) }
          />
        );
      },
      meta: {
        classStr: buildColumnClassString( { width: 5, styles: ColumnModEnums.NO_OVERFLOW } )
      }
    } ),
    columnHelper.accessor( 'name', {
      header: 'Name',
      size: 200,
      cell: info => info.getValue(),
      meta: {
        classStr: buildColumnClassString( { width: 20, styles: ColumnModEnums.NO_OVERFLOW } )
      }
    } ),
    columnHelper.accessor( 'version', {
      header: 'Version',
      size: 100,
      cell: info => {
        const source = info.row.original;
        const sourceIndex = info.row.index;
        return (
          <select
            value={ source.version }
            disabled={ reloadedSourceIndex === sourceIndex }
            onChange={ ( e ) => onChangeDisplayedSourceVersion( sourceIndex, parseInt( e.target.value ) ) }
          >
            {
              _.range( 1, ( source.latest_version_number ?? 0 ) + 1 ).map( ( version ) => (
                <option value={ version } key={ `version-option-${ version }` }>{ version }</option>
              ) )
            }
          </select>
        );
      },
      meta: {
        classStr: buildColumnClassString( { width: 10, styles: ColumnModEnums.CENTER } )
      }
    } ),
    columnHelper.display( {
      id: 'resource',
      header: 'Source',
      size: 450,
      cell: info => {
        const source = info.row.original;
        const resourceIcon = getResourceIcon( source );
        const resourceName: string = getResourceName( source );
        const resourceTitle = `${ resourceName }(${ source.example_count })`;
        return (
          <div className={ styles.source__resource } title={ resourceTitle }>
            <span>{ resourceIcon }</span>
            <span>{ resourceName }</span>
            <span>({ source.example_count })</span>
          </div>
        );
      },
      meta: {
        classStr: buildColumnClassString( { width: 45, styles: ColumnModEnums.NO_OVERFLOW } )
      }
    } ),
    columnHelper.accessor( 'example_type', {
      header: 'Example Type',
      size: 150,
      cell: info => info.getValue(),
      meta: {
        classStr: buildColumnClassString( { width: 15, styles: ColumnModEnums.NO_OVERFLOW } )
      }
    } ),
    columnHelper.display( {
      id: 'peek',
      header: 'Peek',
      size: 50,
      cell: info => {
        const source = info.row.original;
        const sourceIndex = info.row.index;
        return reloadedSourceIndex === sourceIndex ? (
          <Icon
            key={ `source_loader_${ sourceIndex }` }
            icon={ FiLoader }
          />
        ) : (
          <Icon
            key={ `source_action_info_${ source.name }` }
            icon={ PiListMagnifyingGlass }
            className='table_actions_item'
            style={ { cursor: 'pointer' } }
            onClick={ () => onPeekSource( sourceIndex ) }
          />
        );
      },
      meta: {
        classStr: buildColumnClassString( { width: 5, styles: ColumnModEnums.CENTER } )
      }
    } )
  ], [ selectedSourceId, reloadedSourceIndex, onDataSourceSelect, onChangeDisplayedSourceVersion, onPeekSource ] );

  const table = useReactTable( {
    data: sources,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    rowCount,
    state: { pagination },
    onPaginationChange: handlePaginationChange,
    enableSorting: false
  } );

  return <NativeTable table={ table } pagination="server" />;
}

const STEP_CONSTS = {
  GENERAL: 'GENERAL',
  REVIEW: 'REVIEW'
} as const;

const INPUT_CONSTS = {
  // general
  DATA_SOURCE: 'Data Source',
  JSON_DATA: 'JSON Data'
} as const;

const INPUT_MODE = {
  SOURCE: 'source',
  JSON: 'json'
} as const;

type InputModeType = typeof INPUT_MODE[keyof typeof INPUT_MODE];

// Zod schema for JSON validation - snake_case only
// Always expects an array of example objects (even if just one)
const DataSourceExampleMessageSchema = z.object( {
  user: z.enum( [ 'user', 'assistant' ] ),
  message: z.string().min( 1, 'Message cannot be empty' )
} );

const DataSourceExampleSchema = z.object( {
  id: z.string().optional(),
  type: z.enum( [ 'message', 'conversation' ] ),
  data: z.array( DataSourceExampleMessageSchema ).min( 1, 'At least one message is required' ),
  meta_data: z.record( z.string(), z.unknown() ).optional()
} ).refine(
  ( example ) => {
    // Message type must have exactly 2 messages
    if ( example.type === 'message' ) {
      if ( example.data.length !== 2 ) {
        return false;
      }
      // First message must be from user, second from assistant
      if ( example.data[0].user !== 'user' || example.data[1].user !== 'assistant' ) {
        return false;
      }
    }
    // Conversation type must have at least 1 message
    if ( example.type === 'conversation' && example.data.length < 1 ) {
      return false;
    }
    return true;
  },
  {
    message: 'Message type must have exactly 2 messages: first from user, second from assistant. Conversation type must have at least 1 message.'
  }
);

const RunCreateJsonSchema = z.array( DataSourceExampleSchema ).min( 1, 'At least one example is required' );

// Helper function to validate JSON string with Zod
const validateJsonString = ( jsonString?: string ): { valid: boolean; error?: string } => {
  if ( !jsonString || jsonString.trim() === '' ) {
    return { valid: false, error: 'JSON data is required' };
  }

  try {
    const parsed = JSON.parse( jsonString );
    const result = RunCreateJsonSchema.safeParse( parsed );

    if ( !result.success ) {
      return { valid: false, error: z.prettifyError( result.error ) };
    }

    return { valid: true };
  } catch( error ) {
    return { valid: false, error: 'Invalid JSON format' };
  }
};


type Props = {} & MultiStepModalProps<RunCreateReq>;

type State = {
  inputMode: InputModeType;
  selectedSourceId?: string;
  peekIndex?: number;
  sources: Source[];
  sourcesTotal: number;
  reloadedSourceIndex?: number;
  jsonData?: string;
  jsonValidationError?: string;
  includeReason: boolean;
} & MultiStepModalState<typeof STEP_CONSTS, typeof INPUT_CONSTS>;

export default class ModalRunCreate extends MultiStepModal<Props, State, typeof STEP_CONSTS, typeof INPUT_CONSTS, RunCreateReq> {
  static contextType = AuthContext;
  declare context: React.ContextType<typeof AuthContext>;

  constructor( props: Props & MultiStepModalProps<RunCreateReq> ) {
    super( props );
  }

  STEP_CONSTS = STEP_CONSTS;

  INPUT_CONSTS = INPUT_CONSTS;

  inputsByStep = {
    [ this.STEP_CONSTS.GENERAL ]: {
      [ INPUT_CONSTS.DATA_SOURCE ]: {
        validate: () => this.state.inputMode === INPUT_MODE.SOURCE && this.state.selectedSourceId === undefined
      },
      [ INPUT_CONSTS.JSON_DATA ]: {
        validate: () => {
          if ( this.state.inputMode === INPUT_MODE.JSON ) {
            const validation = validateJsonString( this.state.jsonData );
            return !validation.valid;
          }
          return false;
        }
      }
    },
    [ this.STEP_CONSTS.REVIEW ]: {}
  };

  state: State & MultiStepModalState<typeof STEP_CONSTS, typeof INPUT_CONSTS> = {
    inputMode: INPUT_MODE.SOURCE,
    sources: [],
    sourcesTotal: 0,
    includeReason: false,
    step: this.STEP_CONSTS.GENERAL,
    steps: [ this.STEP_CONSTS.GENERAL, this.STEP_CONSTS.REVIEW ],
    validationErrKeys: []
  };

  stepper(): ReactElement {
    const stepMap: Record<string, () => ReactElement> = {
      [ this.STEP_CONSTS.GENERAL ]: this.renderStepGeneral,
      [ this.STEP_CONSTS.REVIEW ]: this.renderStepReview
    };

    return stepMap[ this.state.step ]();
  }

  onComplete = (): void => {
    const {
      cb,
      onClose
    } = this.props;

    const {
      inputMode,
      selectedSourceId,
      sources,
      jsonData,
      includeReason
    } = this.state;

    let ret: RunCreateReq;

    if ( inputMode === INPUT_MODE.SOURCE ) {
      if ( selectedSourceId === undefined ) {
        // should not be possible due to validation, just being explicit here
        throw new Error( 'No data source has been selected.' );
      }

      const selectedSource = sources.find( s => s.id === selectedSourceId );
      if ( !selectedSource ) {
        throw new Error( 'Selected data source not found.' );
      }

      ret = {
        dataSource: {
          dataOrigin: 'reference',
          sourceId: selectedSource.id,
          sourceVersion: selectedSource.version
        },
        includeReason
      };
    } else {
      // JSON mode - expects an array of example objects with snake_case fields
      if ( !jsonData ) {
        throw new Error( 'No JSON data has been provided.' );
      }

      try {
        const parsedData = JSON.parse( jsonData );

        // parsedData is already an array, convert each example to API format
        const examples = parsedData.map( ( example: any ) => ( {
          id: example.id,
          type: example.type,
          data: example.data,
          metaData: example.meta_data
        } ) );

        ret = {
          dataSource: {
            dataOrigin: 'inline',
            examples
          },
          includeReason
        };
      } catch( error ) {
        throw new Error( 'Invalid JSON data provided.' );
      }
    }

    onClose();
    cb( ret );
  };

  async componentDidMount(): Promise<void> {
    await this._fetchAllSourcesData();
  }

  _fetchAllSourcesData = async ( pageIndex = 0, pageSize = 20 ) => {
    try {
      const res = await APIGateway.evals.sourcesSearch( { limit: pageSize, offset: pageIndex * pageSize, is_deprecated: false } );
      this.setState( { sources: res.items, sourcesTotal: res.pagination.total } );
    } catch( e ) {
      console.error( e );
      toast( 'unable to get source' );
    }
  };

  async _fetchSingleSourceData( source_index: number, version: number ) {
    try {
      const sourceId = this.state.sources[ source_index ].id;
      this.setState( { reloadedSourceIndex: source_index } );
      const newSource: SourceGetRes = await APIGateway.evals.sourceGet( sourceId, version );
      this.setState( ( prevState ) => ( {
        sources: [
          ...prevState.sources.slice( 0, source_index ),
          newSource as Source,
          ...prevState.sources.slice( source_index + 1 )
        ]
      } ) );
    } catch( e ) {
      console.error( e );
      toast( 'unable to get source' );
    } finally {
      this.setState( { reloadedSourceIndex: undefined } );
    }
  }

  /* HELPERS */
  _buildDoms = ( obj?: object, parentKey = '' ): ReactElement[] => {
    if( !obj ) return [ <></> ];

    return Object.entries( obj ).map( ( [ key, value ] ) => {
      const fullKey = parentKey ? `${ parentKey }.${ key }` : key;

      if ( typeof value === 'object' && value !== null ) {
        return this._buildDoms( value, fullKey );
      }

      return (
        <div className={ clsx( 'layout__col', styles.config_item ) } key={ `source-key-${ fullKey }` }>
          <fieldset>
            <legend className={ styles.key }>{ fullKey }</legend>
            <p className={ styles.val }>{ value !== undefined && value !== null ? value.toString() : '-' }</p>
          </fieldset>
        </div>
      );
    } ).flat();
  };

  /* HANDLERS */
  onDataSourceSelect = ( sourceId: string ) => {
    this.setState( ( prevState ) => ( {
      selectedSourceId: prevState.selectedSourceId === sourceId ? undefined : sourceId
    } ) );
  };

  onChangeDisplayedSourceVersion = ( source_index: number, version: number ) => {
    this._fetchSingleSourceData( source_index, version );
  };

  /* RENDERS */
  renderStepReview = (): ReactElement => {
    const { onClose } = this.props;
    const {
      inputMode,
      selectedSourceId,
      sources,
      jsonData,
      includeReason
    } = this.state;

    if ( inputMode === INPUT_MODE.SOURCE ) {
      const selectedSource = sources.find( s => s.id === selectedSourceId );
      if( !selectedSource || !sources.length ) return ( <></> );

      const source: Partial<Source> = selectedSource;
      const general = { ...source };
      const config = { ...( source?.config ) };
      delete general.config;

      return (
        <div className={ clsx( 'layout__col', 'pad', styles.layout__review ) }>
          <Heading as="h2" size="md">Review</Heading>
          <div className={ clsx( 'layout__col', 'pad' ) }>
            <p>Are you sure you want to run the evaluation with this data source?</p>
          </div>
          <div className={ clsx( 'layout__row', 'pad' ) }>
            <div className={ clsx( 'layout__col', 'pad' ) }>
              <div className={ clsx( 'layout__row', 'pad' ) }>
                <Heading as="h4" size="md">General Properties</Heading>
              </div>
              <div className={ clsx( 'layout__col', styles.source_pane ) }>
                {
                  this._buildDoms( general )
                }
                <div className={ clsx( 'layout__col', styles.config_item ) }>
                  <fieldset>
                    <legend className={ styles.key }>includeReason</legend>
                    <p className={ styles.val }>{ includeReason }</p>
                  </fieldset>
                </div>
              </div>
            </div>
            <div className={ clsx( 'layout__col', 'pad' ) }>
              <div className={ clsx( 'layout__row', 'pad' ) }>
                <Heading as="h4" size="md">Config Properties</Heading>
              </div>
              <div className={ clsx( 'layout__col', styles.source_pane ) }>
                {
                  this._buildDoms( config )
                }
              </div>
            </div>
          </div>
          <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
            <Button variant='secondary' size='sm' onClick={ onClose }>Cancel</Button>
            <Button variant='secondary' size='sm' onClick={ () => this.stepBack() }>Back</Button>
            <Button variant='primary' size='sm' onClick={ () => this.step() } type='submit'>Run</Button>
          </div>
        </div>
      );
    } else {
      // JSON mode - expects an array of example objects
      let parsedData;
      let exampleCount = 0;

      try {
        parsedData = JSON.parse( jsonData || '[]' );
        exampleCount = Array.isArray( parsedData ) ? parsedData.length : 0;
      } catch( error ) {
        // If parsing fails, we'll show an error message
      }

      return (
        <div className={ clsx( 'layout__col', 'pad', styles.layout__review ) }>
          <Heading as="h2" size="md">Review</Heading>
          <div className={ clsx( 'layout__col', 'pad' ) }>
            <p>Are you sure you want to run the evaluation with this inline JSON data?</p>
          </div>
          <div className={ clsx( 'layout__col', 'pad' ) }>
            <div className={ clsx( 'layout__row', 'pad' ) }>
              <Heading as="h4" size="md">JSON Data Summary</Heading>
            </div>
            <div className={ clsx( 'layout__col', styles.source_pane ) }>
              <div className={ clsx( 'layout__col', styles.config_item ) }>
                <fieldset>
                  <legend className={ styles.key }>Input Mode</legend>
                  <p className={ styles.val }>Inline JSON</p>
                </fieldset>
              </div>
              <div className={ clsx( 'layout__col', styles.config_item ) }>
                <fieldset>
                  <legend className={ styles.key }>Example Count</legend>
                  <p className={ styles.val }>{ exampleCount }</p>
                </fieldset>
              </div>
              <div className={ clsx( 'layout__col', styles.config_item ) }>
                <fieldset>
                  <legend className={ styles.key }>Data Origin</legend>
                  <p className={ styles.val }>inline</p>
                </fieldset>
              </div>
              <div className={ clsx( 'layout__col', styles.config_item ) }>
                <fieldset>
                  <legend className={ styles.key }>Include Reason</legend>
                  <p className={ styles.val }>{ includeReason }</p>
                </fieldset>
              </div>
            </div>
            <div className={ clsx( 'layout__row', 'pad' ) }>
              <Heading as="h4" size="md">Full JSON Data</Heading>
            </div>
            <div className={ clsx( 'layout__col', styles.source_pane ) }>
              <div style={ {
                fontFamily: '"Fira code", "Fira Mono", monospace',
                fontSize: '12px',
                overflow: 'auto',
                maxHeight: '400px',
                backgroundColor: '#f5f5f5',
                padding: '1rem',
                border: '1px solid #ccc',
                borderRadius: '4px',
                lineHeight: '1.5'
              } }
              >
                <pre
                  style={ { margin: 0 } }
                  dangerouslySetInnerHTML={ {
                    __html: highlight( JSON.stringify( parsedData, null, 2 ), languages.json, 'json' )
                  } }
                />
              </div>
            </div>
          </div>
          <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
            <Button variant='secondary' size='sm' onClick={ onClose }>Cancel</Button>
            <Button variant='secondary' size='sm' onClick={ () => this.stepBack() }>Back</Button>
            <Button variant='primary' size='sm' onClick={ () => this.step() } type='submit'>Run</Button>
          </div>
        </div>
      );
    }
  };

  renderJsonInput = (): ReactElement => {
    const { jsonData, jsonValidationError } = this.state;

    const validateAndSetJson = ( code: string ) => {
      // Use Zod validation helper
      const validation = validateJsonString( code );

      // Update both jsonData and jsonValidationError in a single setState
      if ( validation.valid ) {
        this.setState( { jsonData: code, jsonValidationError: undefined } );
      } else {
        this.setState( { jsonData: code, jsonValidationError: validation.error } );
      }
    };

    return (
      <div className={ clsx( 'layout__col', 'pad' ) }>
        <label className={ clsx( 'auth_flow_label', this.inputRequiredCheck( this.INPUT_CONSTS.JSON_DATA ) ) } htmlFor={ this.INPUT_CONSTS.JSON_DATA }>
          { this.INPUT_CONSTS.JSON_DATA }
        </label>
        <div
          data-testid='json-data-input'
          onInput={ ( e: any ) => {
            // Handle direct DOM input events (e.g., from Playwright tests)
            const target = e.target as HTMLTextAreaElement;
            if ( target.tagName === 'TEXTAREA' ) {
              validateAndSetJson( target.value );
            }
          } }
          onChange={ ( e: any ) => {
            // Handle change events as well (for compatibility)
            const target = e.target as HTMLTextAreaElement;
            if ( target.tagName === 'TEXTAREA' ) {
              validateAndSetJson( target.value );
            }
          } }
        >
          <Editor
            value={ jsonData || '' }
            onValueChange={ validateAndSetJson }
            highlight={ ( code ) => highlight( code, languages.json, 'json' ) }
            padding={ 10 }
            style={ {
              fontFamily: '"Fira code", "Fira Mono", monospace',
              fontSize: 12,
              border: '1px solid #ccc',
              borderRadius: '4px',
              minHeight: '650px'
            } }
            placeholder={ `Paste your JSON data here (snake_case, array format required)...

Single example (must be in array):
[
  {
    "id": "optional-id",
    "type": "message",
    "data": [
      {"user": "user", "message": "What's your return policy?"},
      {"user": "assistant", "message": "We offer 30-day returns..."}
    ],
    "meta_data": {"context": "optional"}
  }
]

Multiple examples:
[
  {
    "type": "message",
    "data": [
      {"user": "user", "message": "What's your return policy?"},
      {"user": "assistant", "message": "We offer 30-day returns..."}
    ]
  },
  {
    "type": "conversation",
    "data": [
      {"user": "user", "message": "Question"},
      {"user": "assistant", "message": "Answer"},
      {"user": "user", "message": "Follow-up"},
      {"user": "assistant", "message": "More info"}
    ],
    "meta_data": {"context": "optional"}
  }
]` }
          />
        </div>
        { jsonValidationError && (
          <div className='layout__error' style={ { marginTop: '8px' } } data-testid='json-validation-error'>
            { jsonValidationError }
          </div>
        ) }
      </div>
    );
  };

  renderStepGeneral = (): ReactElement => {
    const { onClose } = this.props;
    const { peekIndex, sources, inputMode, jsonData, selectedSourceId, jsonValidationError } = this.state;

    // Determine if Review button should be disabled - using validation error state
    const isReviewDisabled = inputMode === INPUT_MODE.JSON
      ? ( jsonValidationError !== undefined || !jsonData || jsonData.trim() === '' )
      : ( selectedSourceId === undefined );

    return (
      <>
        {
          ( peekIndex !== undefined ) && (
            <ModalSourceShow
              source={ sources[ peekIndex ] }
              onCloseAction={ () => this.setState( { peekIndex: undefined } ) }
            />
          )
        }
        <Heading as="h2" size="md">Create Run</Heading>
        <div className={ clsx( 'layout__col', 'pad' ) }>
          <label className='auth_flow_label'>Input Mode</label>
          <div className={ clsx( 'layout__row', styles.mode_toggle ) } data-testid='input-mode-toggle'>
            <Button
              variant='secondary'
              size='sm'
              className={ inputMode === INPUT_MODE.SOURCE ? styles.active : '' }
              onClick={ () => this.setState( { inputMode: INPUT_MODE.SOURCE, jsonValidationError: undefined, jsonData: undefined } ) }
              data-testid='mode-source-button'
            >
              Select Source
            </Button>
            <Button
              variant='secondary'
              size='sm'
              className={ inputMode === INPUT_MODE.JSON ? styles.active : '' }
              onClick={ () => this.setState( { inputMode: INPUT_MODE.JSON } ) }
              data-testid='mode-json-button'
            >
              Provide JSON
            </Button>
          </div>
        </div>
        {
          inputMode === INPUT_MODE.SOURCE ? (
            <div className={ clsx( 'layout__col', 'pad', 'overflow_hide' ) }>
              <label className={ clsx( 'auth_flow_label', this.inputRequiredCheck( this.INPUT_CONSTS.DATA_SOURCE ) ) } htmlFor={ this.INPUT_CONSTS.DATA_SOURCE }>{ this.INPUT_CONSTS.DATA_SOURCE }</label>
              <DataSourcesTable
                sources={ this.state.sources }
                selectedSourceId={ this.state.selectedSourceId }
                reloadedSourceIndex={ this.state.reloadedSourceIndex }
                rowCount={ this.state.sourcesTotal }
                onFetchPage={ this._fetchAllSourcesData }
                onDataSourceSelect={ this.onDataSourceSelect }
                onChangeDisplayedSourceVersion={ this.onChangeDisplayedSourceVersion }
                onPeekSource={ ( index ) => this.setState( { peekIndex: index } ) }
              />
            </div>
          ) : (
            this.renderJsonInput()
          )
        }
        <div className={ clsx( 'layout__col', 'pad' ) }>
          <label className='auth_flow_label' style={ { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' } }>
            <input
              type='checkbox'
              checked={ this.state.includeReason }
              onChange={ ( e ) => this.setState( { includeReason: e.target.checked } ) }
              style={ { cursor: 'pointer' } }
            />
            <b>Include evaluation reasoning in results.</b><i>Shows the model’s reasoning for the score it assigns to this run.</i>
          </label>
        </div>
        <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
          <Button variant='secondary' size='sm' onClick={ onClose }>Cancel</Button>
          <Button
            variant='primary'
            size='sm'
            onClick={ () => this.step() }
            type='submit'
            disabled={ isReviewDisabled }
          >
            Review
          </Button>
        </div>
      </>
    );
  };
}
